import { ProgressBar } from "@blueprintjs/core";
import { Button } from "@focus/focusUI";
import type { UploadFile } from "./useUploadQueue";
import {
  getUploadIntent,
  uploadStatusClassName,
  uploadStatusLabel,
} from "./uploadPanelUtils";

interface UploadQueuePanelProps {
  files: UploadFile[];
  onRemoveFile: (id: string) => void;
  onUploadFile: (file: UploadFile) => void;
}

const UploadQueueItem = ({
  file,
  onRemoveFile,
  onUploadFile,
}: {
  file: UploadFile;
  onRemoveFile: (id: string) => void;
  onUploadFile: (file: UploadFile) => void;
}) => (
  <article className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="truncate text-sm font-bold text-slate-950">
          {file.file.name}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold text-slate-500">
          <span>{file.operator}</span>
          <span className="text-slate-300">/</span>
          <span>{(file.file.size / 1000).toFixed(2)} kb</span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span
          className={`rounded-full px-2.5 py-1 text-xs font-semibold uppercase ring-1 ring-inset ${
            uploadStatusClassName[file.status]
          }`}
        >
          {uploadStatusLabel[file.status]}
        </span>
        <Button
          className="focusoc-icon-button focusoc-icon-button-danger"
          icon="trash"
          minimal
          onClick={() => onRemoveFile(file.id)}
          title="Remove file"
        />
        <Button
          className="focusoc-icon-button focusoc-icon-button-success"
          disabled={file.status !== "STAGED"}
          icon="upload"
          minimal
          onClick={() => onUploadFile(file)}
          title="Upload file"
        />
        <Button
          className="focusoc-icon-button"
          disabled={file.status !== "ERROR"}
          icon="refresh"
          minimal
          onClick={() => onUploadFile(file)}
          title="Retry upload"
        />
      </div>
    </div>
    <ProgressBar
      className="mt-3"
      value={file.progress / 100}
      stripes={file.status === "UPLOADING"}
      intent={getUploadIntent(file.status)}
    />
  </article>
);

export const UploadQueuePanel = ({
  files,
  onRemoveFile,
  onUploadFile,
}: UploadQueuePanelProps) => (
  <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white">
    <header className="flex items-center justify-between gap-3 border-b border-slate-200 bg-slate-50/70 px-4 py-3">
      <div>
        <div className="text-xs font-semibold uppercase text-slate-500">
          Queue
        </div>
        <h3 className="mt-1 text-sm font-bold text-slate-950">
          Files ready to upload
        </h3>
      </div>
      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold uppercase text-slate-600 ring-1 ring-inset ring-slate-200">
        {files.length} files
      </span>
    </header>

    <div className="min-h-0 flex-1 overflow-y-auto p-3">
      {files.length === 0 && (
        <div className="flex h-full min-h-[160px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50/70 p-6 text-center text-sm font-semibold text-slate-500">
          No files staged
        </div>
      )}

      <div className="flex flex-col gap-3">
        {files.map((file) => (
          <UploadQueueItem
            file={file}
            key={file.id}
            onRemoveFile={onRemoveFile}
            onUploadFile={onUploadFile}
          />
        ))}
      </div>
    </div>
  </section>
);

